import React, { useState, useEffect, useCallback } from "react";
import LineChats from "../graphs/LineChats";
import PieChats from "../graphs/PieChats";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { useAnalytics } from "../../hooks/useAnalytics";
import RankedTable from "../layout/RankedTable";
import { FaChartBar, FaUsers, FaUserTag, FaEye, FaUserFriends, FaArrowUp, FaArrowDown, FaSync } from "react-icons/fa";

const Analtyics = (props) => {
  const today = new Date().toISOString().split('T')[0];
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [selectedReport, setSelectedReport] = useState('');
  const [selectedPersona, setSelectedPersona] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedLineChart, setSelectedLineChart] = useState('');

  const { loading, kpis, filters, charts, tables, fetchAnalytics } = useAnalytics();

  const applyFilters = useCallback(() => {
    fetchAnalytics({
      startDate:  dateFrom,
      endDate:    dateTo,
      reportId:   selectedReport  || undefined,
      personaId:  selectedPersona || undefined,
      userEmail:  selectedUser    || undefined,
    });
  }, [dateFrom, dateTo, selectedReport, selectedPersona, selectedUser, fetchAnalytics]);

  // Load on mount
  useEffect(() => {
    applyFilters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lineKeys  = charts.byGroup ? Object.keys(charts.byGroup) : [];
  const pieKeys   = lineKeys.filter(k => k !== "MGS Labs Global Old");

  // Support both flat numbers (old backend) and {value, trend} objects (new backend)
  const kpiVal = (field) => typeof kpis[field] === 'object' ? kpis[field]?.value : kpis[field];
  const kpiTrend = (field) => typeof kpis[field] === 'object' ? kpis[field]?.trend : null;

  const kpiCards = [
    { label: "Total Reports",   value: kpiVal("totalReports"),      icon: <FaChartBar size={20} />,    color: "text-blue-500",    bg: "bg-blue-50",    trend: kpiTrend("totalReports")      || { value: null, up: null, label: "last 30 days" } },
    { label: "Total Personas",  value: kpiVal("totalPersonas"),     icon: <FaUserTag size={20} />,     color: "text-emerald-500", bg: "bg-emerald-50", trend: kpiTrend("totalPersonas")     || { value: null, up: null, label: "last 30 days" } },
    { label: "Total Users",     value: kpiVal("totalUsers"),        icon: <FaUserFriends size={20} />, color: "text-rose-500",    bg: "bg-rose-50",    trend: kpiTrend("totalUsers")        || { value: null, up: null, label: "last 30 days" } },
    { label: "Active Users",    value: kpiVal("totalActiveUsers"),  icon: <FaUsers size={20} />,       color: "text-amber-500",   bg: "bg-amber-50",   trend: kpiTrend("totalActiveUsers")  || { value: null, up: null, label: "last 30 days" } },
    { label: "Total Views",     value: kpiVal("totalViews"),        icon: <FaEye size={20} />,         color: "text-violet-500",  bg: "bg-violet-50",  trend: kpiTrend("totalViews")        || { value: null, up: null, label: "last 30 days" } },
  ];

  const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-4 p-3 bg-gray-100" style={{alignContent:"center",display:"grid"}}>
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
    </div>
  );

  const selectCls = "text-xs border border-gray-200 rounded-lg px-3 py-1.5 bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-100";

  return (
    <div className="bg-gray-50 p-6 rounded-xl space-y-6">

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Analytics Dashboard</h2>
          <p className="text-xs text-gray-400 mt-1">Track usage, engagement, and activity across all groups</p>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-wrap items-end gap-4">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-400 font-medium">From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className={selectCls} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-400 font-medium">To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className={selectCls} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-400 font-medium">Report</span>
          <select className={selectCls} value={selectedReport} onChange={e => setSelectedReport(e.target.value)}>
            <option value="">All Reports</option>
            {filters.reports.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-400 font-medium">Persona</span>
          <select className={selectCls} value={selectedPersona} onChange={e => setSelectedPersona(e.target.value)}>
            <option value="">All Personas</option>
            {filters.personas.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-gray-400 font-medium">User</span>
          <select className={selectCls} value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
            <option value="">All Users</option>
            {filters.users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <button
          onClick={applyFilters}
          disabled={loading}
          className="flex items-center gap-2 px-4 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors self-end"
          style={{ height: "40px" }}
        >
          <FaSync size={10} className={loading ? "animate-spin" : ""} />
          {loading ? "Loading..." : "Apply"}
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-5 gap-4">
        {kpiCards.map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-gray-400 font-medium">{kpi.label}</span>
              <div className={`${kpi.bg} ${kpi.color} p-2 rounded-lg`}>{kpi.icon}</div>
            </div>
            <div className="text-2xl font-bold text-gray-800">
              {loading ? <div className="h-7 w-12 bg-gray-100 rounded animate-pulse" /> : (kpi.value ?? "—")}
            </div>
            <div className="flex items-center gap-1 mt-1.5 text-xs">
              {kpi.trend?.value && (
                <span className={`font-semibold ${kpi.trend.up ? "text-emerald-500" : "text-rose-500"}`}>
                  {kpi.trend.up ? <FaArrowUp size={9} className="inline mr-0.5" /> : <FaArrowDown size={9} className="inline mr-0.5" />}
                  {kpi.trend.value}
                </span>
              )}
              <span className="text-gray-400">last 30 days</span>
            </div>
          </div>
        ))}
      </div>

      {/* Bar Chart — Daily Views */}
      <div className="bg-white rounded-[5px] shadow-sm border border-gray-100">
        <SectionTitle title="Daily Activity" subtitle="Total report views per day over the selected date range" />
        {charts.byDate?.dates?.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={charts.byDate.dates.map((d, i) => ({ date: d, views: charts.byDate.views[i] ?? 0 }))}
              margin={{ top: 4, right: 20, left: 0, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ fontSize: "12px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                cursor={{ fill: "#f3f4f6" }}
              />
              <Bar dataKey="views" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Views" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[200px] flex items-center justify-center text-gray-400 text-sm">
            No activity data for the selected range
          </div>
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-2 bg-white rounded-[5px] shadow-sm border border-gray-100">
          <SectionTitle title="Report Views Over Time" subtitle="Trend of report access activity by user group" />
          <span className="p-2 block">
                      <LineChats jsonData={charts.byGroup} selectedChart={selectedLineChart} />

          </span>
        </div>
        <div className="bg-white rounded-[5px] shadow-sm border border-gray-100">
          <SectionTitle title="Report Distribution" subtitle="Breakdown of clicks per user group" />
                    <span className="p-2 block">

          <PieChats data={charts.byGroup} selectedCategory={pieKeys[0]} />
          </span>
        </div>
      </div>

      {/* Top Reports & Visuals */}
      <div className="grid grid-cols-2 gap-4">
        <RankedTable
          title="Top Reports by Views"
          subtitle="Most accessed reports in selected period"
          badge="Top 10"
          badgeColor="blue"
          columns={[{ label: "#" }, { label: "Report" }, { label: "Group" }, { label: "Views", align: "right" }]}
          rows={tables.topReports}
          renderRow={(r, i) => (<>
            <td className="px-5 py-3 text-gray-400 font-medium">{i + 1}</td>
            <td className="px-5 py-3 font-medium text-gray-800 max-w-[150px]"><span className="truncate block" title={r.reportName}>{r.reportName}</span></td>
            <td className="px-5 py-3"><span className="bg-indigo-50 text-indigo-600 text-[10px] font-semibold px-2 py-0.5 rounded-full">{r.userGroupName}</span></td>
            <td className="px-5 py-3 text-right">
              <div className="flex items-center justify-end gap-2">
                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (r.count / (tables.topReports[0]?.count || 1)) * 100)}%` }} />
                </div>
                <span className="font-bold text-gray-700 w-6 text-right">{r.count}</span>
              </div>
            </td>
          </>)}
        />

        <RankedTable
          title="Top Custom Visuals"
          subtitle="Most selected KPIs and visuals"
          badge="Top 10"
          badgeColor="emerald"
          columns={[{ label: "#" }, { label: "Visual" }, { label: "Group" }, { label: "Uses", align: "right" }]}
          rows={tables.topVisuals}
          renderRow={(r, i) => (<>
            <td className="px-5 py-3 text-gray-400 font-medium">{i + 1}</td>
            <td className="px-5 py-3 font-medium text-gray-800 max-w-[150px]"><span className="truncate block" title={r.title}>{r.title}</span></td>
            <td className="px-5 py-3"><span className="bg-emerald-50 text-emerald-600 text-[10px] font-semibold px-2 py-0.5 rounded-full">{r.userGroupName}</span></td>
            <td className="px-5 py-3 text-right">
              <div className="flex items-center justify-end gap-2">
                <div className="w-16 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (r.saveCount / (tables.topVisuals[0]?.saveCount || 1)) * 100)}%` }} />
                </div>
                <span className="font-bold text-gray-700 w-6 text-right">{r.saveCount}</span>
              </div>
            </td>
          </>)}
        />
      </div>

      {/* User Activity */}
      <RankedTable
        title="User Activity"
        subtitle="Report views and engagement per user"
        badge="Top 50"
        badgeColor="violet"
        columns={[{ label: "#" }, { label: "Email" }, { label: "Type" }, { label: "Views", align: "right" }, { label: "Last Activity" }]}
        rows={tables.userActivity}
        renderRow={(u, i) => (<>
          <td className="px-5 py-3 text-gray-400 font-medium">{i + 1}</td>
          <td className="px-5 py-3 text-gray-700 font-medium">{u.email}</td>
          <td className="px-5 py-3">
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
              u.userType === "Admin" ? "bg-blue-100 text-blue-700" :
              u.userType === "Field" ? "bg-amber-100 text-amber-700" :
              "bg-gray-100 text-gray-600"
            }`}>{u.userType || "—"}</span>
          </td>
          <td className="px-5 py-3 text-right">
            <div className="flex items-center justify-end gap-2">
              <div className="w-20 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                <div className="bg-violet-500 h-1.5 rounded-full" style={{ width: `${Math.min(100, (u.views / (tables.userActivity[0]?.views || 1)) * 100)}%` }} />
              </div>
              <span className="font-bold text-gray-700 w-6 text-right">{u.views}</span>
            </div>
          </td>
          <td className="px-5 py-3 text-gray-400">{u.lastActivity ? u.lastActivity.slice(0, 10) : "—"}</td>
        </>)}
      />

    </div>
  );
};

export default Analtyics;
